'use client'

import { Button } from '@carbon/react'
import { User } from '@carbon/react/icons'
import type { KmerLocale } from '@kmerhosting/i18n'
import { useEffect, useMemo, useState } from 'react'

const dashUrl = 'https://dash.kmerhosting.com'
const dashOrigin = 'https://dash.kmerhosting.com'
const authBridgeUrl = `${dashUrl}/auth-state/`
const authStateMessageType = 'kmerhosting:auth-state'
const authHintCookie = 'kmerhosting_auth_hint'

const signInLabels: Record<KmerLocale, string> = {
  en: 'Sign in',
  fr: 'Se connecter',
  es: 'Iniciar sesión',
  pt: 'Iniciar sessão',
  de: 'Anmelden',
  'zh-Hans': '登录',
  ar: 'تسجيل الدخول',
  hi: 'साइन इन',
  bn: 'সাইন ইন',
  id: 'Masuk',
  ja: 'ログイン',
  ru: 'Войти',
  it: 'Accedi',
  ko: '로그인',
  tr: 'Giriş yap',
  vi: 'Đăng nhập',
  ur: 'سائن ان',
  nl: 'Inloggen',
  pl: 'Zaloguj się',
  fa: 'ورود',
}

type AuthState = {
  authenticated: boolean
  fullName?: string
  avatarUrl?: string | null
  email?: string
  creditCents?: number | null
  creditUpdatedAt?: string | null
}

const profileLabels: Partial<Record<KmerLocale, { email: string; credit: string; account: string; addCredit: string }>> = {
  en: { email: 'Email', credit: 'Account credit', account: 'Account settings', addCredit: 'Add credit' },
  fr: { email: 'E-mail', credit: 'Crédit du compte', account: 'Paramètres du compte', addCredit: 'Ajouter du crédit' },
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return ''
  return `${parts[0]?.[0] || ''}${parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : ''}`.toUpperCase()
}

function readAuthHintCookie(): AuthState {
  if (typeof document === 'undefined') return { authenticated: false }
  const entry = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(`${authHintCookie}=`))
  if (!entry) return { authenticated: false }
  try {
    const value = JSON.parse(decodeURIComponent(entry.slice(authHintCookie.length + 1))) as Record<string, unknown>
    const expiresAt = typeof value.expiresAt === 'number' ? value.expiresAt : null
    if (value.authenticated !== true || (expiresAt && expiresAt <= Math.floor(Date.now() / 1000))) return { authenticated: false }
    return {
      authenticated: true,
      fullName: typeof value.fullName === 'string' ? value.fullName : undefined,
      avatarUrl: typeof value.avatarUrl === 'string' && value.avatarUrl ? value.avatarUrl : null,
      email: typeof value.email === 'string' ? value.email : undefined,
      creditCents: typeof value.creditCents === 'number' && Number.isFinite(value.creditCents) ? value.creditCents : null,
      creditUpdatedAt: typeof value.creditUpdatedAt === 'string' ? value.creditUpdatedAt : null,
    }
  } catch {
    return { authenticated: false }
  }
}

export function SiteAccountState({
  locale,
  dashLabel,
  accountLabel,
  closeSideNav,
}: {
  locale: KmerLocale
  dashLabel: string
  accountLabel: string
  closeSideNav: () => void
}) {
  const [state, setState] = useState<AuthState>({ authenticated: false })
  const [profileOpen, setProfileOpen] = useState(false)
  const fallbackInitials = useMemo(() => initials(state.fullName || ''), [state.fullName])
  const labels = profileLabels[locale] || profileLabels.en!
  const credit = state.creditCents == null
    ? '—'
    : new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(state.creditCents / 100)

  useEffect(() => {
    const refreshCookieHint = () => {
      setState(readAuthHintCookie())
    }

    const receiveAuthState = (event: MessageEvent) => {
      if (event.origin !== dashOrigin) return
      const payload = event.data as Record<string, unknown> | null
      if (!payload || payload.type !== authStateMessageType || typeof payload.authenticated !== 'boolean') return
      setState({
        authenticated: payload.authenticated,
        fullName: typeof payload.fullName === 'string' ? payload.fullName : undefined,
        avatarUrl: typeof payload.avatarUrl === 'string' && payload.avatarUrl ? payload.avatarUrl : null,
        email: typeof payload.email === 'string' ? payload.email : undefined,
        creditCents: typeof payload.creditCents === 'number' && Number.isFinite(payload.creditCents) ? payload.creditCents : null,
        creditUpdatedAt: typeof payload.creditUpdatedAt === 'string' ? payload.creditUpdatedAt : null,
      })
    }

    refreshCookieHint()
    window.addEventListener('message', receiveAuthState)
    window.addEventListener('focus', refreshCookieHint)
    window.addEventListener('pageshow', refreshCookieHint)
    return () => {
      window.removeEventListener('message', receiveAuthState)
      window.removeEventListener('focus', refreshCookieHint)
      window.removeEventListener('pageshow', refreshCookieHint)
    }
  }, [])

  useEffect(() => {
    if (!profileOpen) return
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Element | null
      if (!target?.closest('.site-header__profile-menu')) setProfileOpen(false)
    }
    document.addEventListener('click', closeOnOutsideClick)
    return () => document.removeEventListener('click', closeOnOutsideClick)
  }, [profileOpen])

  return (
    <>
      <iframe
        aria-hidden="true"
        className="site-header__auth-state-bridge"
        src={authBridgeUrl}
        tabIndex={-1}
        title=""
      />

      <div className="site-header__account-state">
        {state.authenticated ? (
          <>
            <Button
              className="site-header__dash-button"
              href={`${dashUrl}/`}
              kind="primary"
              onClick={closeSideNav}
              size="sm"
            >
              {dashLabel}
            </Button>
            <div className="site-header__profile-menu">
              <Button
                aria-expanded={profileOpen}
                aria-haspopup="dialog"
                aria-label={state.fullName ? `${accountLabel}: ${state.fullName}` : accountLabel}
                className="site-header__profile-link"
                onClick={() => setProfileOpen((open) => !open)}
                title={state.fullName || accountLabel}
                type="button"
                kind="ghost"
                size="sm"
              >
                {state.avatarUrl ? (
                  <img alt="" className="site-header__profile-image" referrerPolicy="no-referrer" src={state.avatarUrl} />
                ) : fallbackInitials ? (
                  <span aria-hidden="true" className="site-header__profile-initials">{fallbackInitials}</span>
                ) : (
                  <User aria-hidden="true" size={18} />
                )}
              </Button>
              {profileOpen ? (
                <div aria-label={accountLabel} className="site-header__profile-popover" role="dialog">
                  <div className="site-header__profile-popover-identity">
                    <strong>{state.fullName || accountLabel}</strong>
                    <span>{state.email || '—'}</span>
                  </div>
                  <div className="site-header__profile-popover-credit">
                    <span>{labels.credit}</span>
                    <strong>{credit}</strong>
                  </div>
                  <div className="site-header__profile-popover-actions">
                    <Button href={`${dashUrl}/?view=account`} kind="ghost" onClick={() => { closeSideNav(); setProfileOpen(false) }} size="sm">{labels.account}</Button>
                    <Button href={`${dashUrl}/?view=credit`} kind="primary" onClick={() => { closeSideNav(); setProfileOpen(false) }} size="sm">{labels.addCredit}</Button>
                  </div>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <Button
            className="site-header__dash-button"
            href={`${dashUrl}/login`}
            kind="primary"
            onClick={closeSideNav}
            size="sm"
          >
            {signInLabels[locale] || signInLabels.en}
          </Button>
        )}
      </div>
    </>
  )
}
